import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function DepreciationCalculator() {
  const [assetCost, setAssetCost] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Finance", url: "https://calclab.app/finance" },
      { name: "Depreciation Calculator", url: "https://calclab.app/calculator/depreciation" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Depreciation Calculator",
      "Calculate asset depreciation using straight-line or declining balance methods. Free online finance calculator.",
      "https://calclab.app/calculator/depreciation",
      lang
    )
  ]);
  const [salvageValue, setSalvageValue] = useState("");
  const [usefulLife, setUsefulLife] = useState("");
  const [method, setMethod] = useState("straight-line");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const cost = parseFloat(assetCost);
    const salvage = parseFloat(salvageValue);
    const life = parseFloat(usefulLife);

    if (isNaN(cost) || isNaN(salvage) || isNaN(life) || cost <= 0 || salvage < 0 || life <= 0 || salvage >= cost) {
      toast.error("Please enter valid values (salvage value must be less than asset cost)");
      return;
    }

    let annualDepreciation = 0;
    
    if (method === "straight-line") {
      annualDepreciation = (cost - salvage) / life;
    } else if (method === "declining-balance") {
      const rate = 2 / life; // Double declining
      annualDepreciation = cost * rate;
    }

    setResult(annualDepreciation);
    toast.success("Depreciation calculated!");
  };

  const reset = () => {
    setAssetCost("");
    setSalvageValue("");
    setUsefulLife("");
    setMethod("straight-line");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Depreciation Calculator</CardTitle>
          <CardDescription>
            Calculate asset depreciation using different methods
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assetCost">Asset Cost ($)</Label>
            <Input
              id="assetCost"
              type="number"
              placeholder="50000"
              value={assetCost}
              onChange={(e) => setAssetCost(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="salvageValue">Salvage Value ($)</Label>
            <Input
              id="salvageValue"
              type="number"
              placeholder="5000"
              value={salvageValue}
              onChange={(e) => setSalvageValue(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="usefulLife">Useful Life (years)</Label>
            <Input
              id="usefulLife"
              type="number"
              placeholder="10"
              value={usefulLife}
              onChange={(e) => setUsefulLife(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="method">Depreciation Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="straight-line">Straight-Line</SelectItem>
                <SelectItem value="declining-balance">Declining Balance (Double)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg break-all overflow-wrap-anywhere">
                    <strong>Annual Depreciation:</strong> ${result.toFixed(2)}
                  </p>
                  <CopyResultButton value={result.toFixed(2)} />
                </div>
              </div>
              
              <CalculatorSuperCalcCTA config={calculatorCTAs["depreciation"]} trigger={result} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
